import { useEffect, useMemo } from 'react';
import { Navigate, useParams, useSearchParams } from 'react-router-dom';

import { APP_ROUTE } from '@backrs_ui/grow-ui/src/appRoutes.constants';
import { LUBAV_SUPERSESSIONS, SearchedUser } from '@backrs_ui/grow-ui/src/models/models';
import { clearMemberSolutionSession } from '@backrs_ui/grow-ui/src/redux/coachingDashboard/memberSolutionSession';
import { useGetOfficeHoursSessionDataQuery } from '@backrs_ui/grow-ui/src/redux/coachLubav/coachLubav.api';
import {
  initiateSession,
  loadOfficeHoursSessionData,
  resetOfficeHoursSession,
  setCoachingUserWithDocuments,
} from '@backrs_ui/grow-ui/src/redux/coachLubav/coachLubav.thunk';
import { useAppDispatch, useAppSelector } from '@backrs_ui/grow-ui/src/redux/hooks';
import { setActiveSessionType } from '@backrs_ui/grow-ui/src/redux/lubavSessionState/lubavSessionState';
import { getSessionState } from '@backrs_ui/grow-ui/src/redux/lubavSessionState/lubavSessionState.utils';
import { closeCustomTab } from '@backrs_ui/grow-ui/src/redux/tabs/tabs.slice';
import { selectUserId } from '@backrs_ui/grow-ui/src/redux/user/user';
import { personName } from '@backrs_ui/grow-ui/src/utils/personName';

import { skipToken } from '@reduxjs/toolkit/query';

import { useAgentContext } from './MemberDetailPage.agentContext';
import { useCurrentMember } from './useCurrentMember';

const ACTIVE_SESSION = LUBAV_SUPERSESSIONS.SUPERCOACH;

/**
 * Coaching dashboard member detail page. Renders nothing in the page-content slot — the
 * MEMBER_* tab content is contributed by the InfoBox empty-state map (see infoBoxEmptyStates).
 * This component's job is to bootstrap the supercoach Lubav session for the current member
 * and register the member as agent context.
 */
export const MemberDetailPage = () => {
  const dispatch = useAppDispatch();
  const userId = useAppSelector(selectUserId);
  const { memberId } = useParams<{ memberId: string }>();
  const { member } = useCurrentMember();

  const [searchParams] = useSearchParams();
  const sessionIdParam = searchParams.get('session_id');
  const querySessionId =
    sessionIdParam && Number.isFinite(Number(sessionIdParam)) ? Number(sessionIdParam) : null;

  const sessionId = useAppSelector(
    (state) => getSessionState(state, ACTIVE_SESSION).coachLubav.sessionId,
  );

  const { data: recalledSessionData } = useGetOfficeHoursSessionDataQuery(
    querySessionId !== null ? { session_id: querySessionId } : skipToken,
  );

  useAgentContext(member);

  const searchedUser: SearchedUser | undefined = useMemo(
    () =>
      member
        ? {
            id: member.id.toString(),
            name: personName(member),
            relevance: 0,
            profile_image: member.profile_image || undefined,
          }
        : undefined,
    [member],
  );

  useEffect(() => {
    if (!searchedUser) return;

    dispatch(setActiveSessionType(ACTIVE_SESSION));
    dispatch(setCoachingUserWithDocuments({ user: searchedUser, sessionType: ACTIVE_SESSION }));
  }, [dispatch, searchedUser]);

  useEffect(() => {
    if (!sessionId && userId && searchedUser && !querySessionId) {
      dispatch(
        initiateSession({
          path: '/coaching-dashboard',
          officeHoursId: 'coaching_dashboard',
          activeSession: ACTIVE_SESSION,
          coachingUserId: Number(searchedUser.id),
        }),
      )
        .unwrap()
        .catch((err) => console.error('Failed to initiate supercoach session:', err));
    }
  }, [dispatch, userId, sessionId, searchedUser, querySessionId]);

  useEffect(() => {
    if (recalledSessionData) {
      dispatch(
        loadOfficeHoursSessionData({
          sessionData: recalledSessionData,
          activeSession: ACTIVE_SESSION,
        }),
      );
    }
  }, [recalledSessionData, dispatch]);

  useEffect(() => {
    return () => {
      dispatch(resetOfficeHoursSession({ activeSession: ACTIVE_SESSION }));
      dispatch(setActiveSessionType(null));
      dispatch(closeCustomTab('memberSolutionSession'));
      dispatch(closeCustomTab('memberBlueprint'));
      dispatch(closeCustomTab('memberAyot'));
      dispatch(closeCustomTab('memberLifeReadiness'));
      dispatch(clearMemberSolutionSession());
    };
  }, [dispatch, memberId]);

  if (memberId !== undefined && isNaN(Number(memberId))) {
    return <Navigate to={APP_ROUTE.COACHING_DASHBOARD} replace />;
  }

  return null;
};
